import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Music2, CheckCircle2 } from "lucide-react";
import { resolveGuideAssetUrl } from "../utils/resolveGuideAssetUrl";
import "./SocialsPage.css";
import RecentTikToks from "./RecentTikToks";

const socials = [
  { name: "TikTok", handle: "@valovate_val", href: "https://www.tiktok.com/@valovate_val", description: "Quick tips. Better plays.", icon: null },
  { name: "YouTube", handle: "@Valovate", href: "https://www.youtube.com/@Valovate", description: "Go deeper into the game.", icon: "youtube.svg" },
  { name: "Instagram", handle: "@valovate_val", href: "https://www.instagram.com/valovate_val", description: "Fresh clips & updates.", icon: "instagram.svg" },
  { name: "Discord", handle: "Join the community", href: "https://discord.gg/GrCvYvqFVF", description: "Find your people. Improve together.", icon: "discord.svg" },
  { name: "Twitch", handle: "@valovate_val", href: "https://www.twitch.tv/valovate_val", description: "Catch us live.", icon: "twitch.svg" },
  { name: "X", handle: "@valovate", href: "https://x.com/valovate", description: "Stay in the loop.", icon: "x_twt.svg" },
];

function SubscribeForm() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  async function subscribe(event) {
    event.preventDefault();
    if (status === "pending") return;
    const data = new FormData(event.currentTarget);
    setStatus("pending");
    setError("");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch("/api/socials/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({ email: data.get("email"), consent: data.get("consent") === "on", website: data.get("website") }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.ok !== true) throw new Error(response.status === 429 ? "Too many attempts. Please try again later." : "We couldn’t save your signup. Please try again shortly.");
      setStatus("success");
    } catch (err) {
      setError(err.name === "AbortError" ? "The request timed out. Please try again." : err.message);
      setStatus("error");
    } finally { clearTimeout(timeout); }
  }
  return (
    <section className="socials-subscribe" aria-labelledby="socials-subscribe-title">
      <span className="socials-mail"><Mail size={22} aria-hidden="true" /></span>
      <span className="socials-eyebrow">BE PART OF WHAT’S NEXT</span>
      <h2 id="socials-subscribe-title">Subscribe to Valovate</h2>
      <p>Sign up to receive exclusive email updates and become a beta tester for the Valovate.com team improvement website.</p>
      {status === "success" ? <div className="socials-success" role="status"><CheckCircle2 aria-hidden="true" /><div><strong>You’re on the list.</strong><p>Thanks for signing up for updates and beta testing opportunities.</p></div></div> : (
        <form onSubmit={subscribe}>
          <label htmlFor="socials-email">Email address</label>
          <div className="socials-input-row"><input id="socials-email" name="email" type="email" autoComplete="email" placeholder="you@example.com" maxLength={254} required disabled={status === "pending"} aria-describedby={error ? "socials-signup-error" : undefined} /><button className="socials-button" disabled={status === "pending"} type="submit">{status === "pending" ? "Joining…" : "Count me in"}<ArrowUpRight size={18} aria-hidden="true" /></button></div>
          <div className="socials-honeypot" aria-hidden="true"><label>Leave this empty<input name="website" tabIndex={-1} autoComplete="off" /></label></div>
          <label className="socials-consent"><input name="consent" type="checkbox" required disabled={status === "pending"} /><span>I agree to receive Valovate updates and beta invitations by email. See our <Link to="/privacy-policy">Privacy Policy</Link>. To opt out, email <a href="mailto:contact.admin@valovate.com">contact.admin@valovate.com</a>.</span></label>
          {error && <p id="socials-signup-error" className="socials-error" role="alert">{error}</p>}
        </form>
      )}
    </section>
  );
}

export default function SocialsPage() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Valovate | Socials & Community";
    return () => { document.title = previous; };
  }, []);
  return (
    <div className="socials-page">
      <div className="socials-shell">
        <header className="socials-hero">
          <Link to="/" aria-label="Valovate home" className="socials-logo"><img src={resolveGuideAssetUrl("/assets/valovate_logo_full.svg")} alt="Valovate" /></Link>
          <span className="socials-eyebrow">THE GAME IS BETTER TOGETHER</span>
          <h1>Find us. Follow along.<br /><span>Level up.</span></h1>
          <p>Your next play starts here. Guides, clips, and a community<br className="socials-desktop-break" /> built around getting better at VALORANT.</p>
          <Link to="/" className="socials-site-link">Explore Valovate.com <ArrowUpRight size={17} aria-hidden="true" /></Link>
        </header>
        <nav className="socials-links" aria-label="Valovate social channels">
          {socials.map((social) => <a key={social.name} className="socials-link-card" href={social.href} target="_blank" rel="noopener noreferrer"><span className="socials-platform-icon">{social.icon ? <img src={resolveGuideAssetUrl(`/assets/SVG-Icons/${social.icon}`)} alt="" /> : <Music2 size={25} aria-hidden="true" />}</span><span className="socials-link-copy"><strong>{social.name}</strong><span>{social.description}</span><small>{social.handle}</small></span><ArrowUpRight className="socials-link-arrow" size={20} aria-hidden="true" /></a>)}
        </nav>
        <SubscribeForm />
        <RecentTikToks />
        <footer className="socials-footer"><Link to="/">VALOVATE</Link><span>Play with purpose.</span><div><Link to="/privacy-policy">Privacy</Link><Link to="/terms-of-service">Terms</Link><a href="mailto:contact.admin@valovate.com">Contact</a></div></footer>
      </div>
    </div>
  );
}

